import React, { useState, useEffect } from 'react'
import { format, startOfWeek, endOfWeek, isWithinInterval } from 'date-fns'
import { auth, db } from '../firebase/config'
import { collection, addDoc, query, where, orderBy, getDocs, updateDoc, doc } from 'firebase/firestore'

const PerformanceTracker = ({ locations }) => {
  const [performanceData, setPerformanceData] = useState([])
  const [showAddForm, setShowAddForm] = useState(false)
  const [selectedTimeFrame, setSelectedTimeFrame] = useState('week')
  const [newEntry, setNewEntry] = useState({
    locationId: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    sales: '',
    customers: '',
    weather: 'sunny',
    notes: '',
    startTime: '11:00',
    endTime: '15:00'
  })

  useEffect(() => {
    fetchPerformanceData()
  }, [selectedTimeFrame])

  const fetchPerformanceData = async () => {
    try {
      const q = query(
        collection(db, 'performance'),
        where('userId', '==', auth.currentUser.uid),
        orderBy('date', 'desc')
      )
      const querySnapshot = await getDocs(q)
      const data = []
      querySnapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() })
      })
      setPerformanceData(data)
    } catch (error) {
      console.error('Error fetching performance data:', error)
    }
  }

  const addPerformanceEntry = async (e) => {
    e.preventDefault()
    
    try {
      await addDoc(collection(db, 'performance'), {
        ...newEntry,
        userId: auth.currentUser.uid,
        sales: parseFloat(newEntry.sales),
        customers: parseInt(newEntry.customers),
        date: new Date(newEntry.date),
        createdAt: new Date()
      })
      
      setNewEntry({
        locationId: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        sales: '',
        customers: '',
        weather: 'sunny',
        notes: '',
        startTime: '11:00',
        endTime: '15:00'
      })
      setShowAddForm(false)
      fetchPerformanceData()
    } catch (error) {
      console.error('Error adding performance entry:', error)
    }
  }

  const getLocationName = (locationId) => {
    const location = locations.find(loc => loc.id === locationId)
    return location ? location.name : 'Unknown Location'
  }

  const getWeatherIcon = (weather) => {
    const icons = {
      'sunny': '☀️',
      'cloudy': '☁️',
      'rainy': '🌧️',
      'stormy': '⛈️',
      'snowy': '❄️'
    }
    return icons[weather] || '🌤️'
  }

  const calculateStats = () => {
    const now = new Date()
    let filteredData = performanceData

    if (selectedTimeFrame === 'week') {
      const start = startOfWeek(now)
      const end = endOfWeek(now)
      filteredData = performanceData.filter(entry => 
        isWithinInterval(entry.date.toDate ? entry.date.toDate() : new Date(entry.date), { start, end })
      )
    } else if (selectedTimeFrame === 'month') {
      const currentMonth = now.getMonth()
      filteredData = performanceData.filter(entry => {
        const entryDate = entry.date.toDate ? entry.date.toDate() : new Date(entry.date)
        return entryDate.getMonth() === currentMonth
      })
    }

    const totalSales = filteredData.reduce((sum, entry) => sum + (entry.sales || 0), 0)
    const totalCustomers = filteredData.reduce((sum, entry) => sum + (entry.customers || 0), 0)
    const avgSales = filteredData.length > 0 ? totalSales / filteredData.length : 0
    const avgCustomers = filteredData.length > 0 ? totalCustomers / filteredData.length : 0

    return {
      totalSales,
      totalCustomers,
      avgSales,
      avgCustomers,
      totalDays: filteredData.length
    }
  }

  const findBestLocations = () => {
    const locationStats = {}
    
    performanceData.forEach(entry => {
      if (!locationStats[entry.locationId]) {
        locationStats[entry.locationId] = {
          locationId: entry.locationId,
          name: getLocationName(entry.locationId),
          totalSales: 0,
          totalCustomers: 0,
          days: 0
        }
      }
      
      locationStats[entry.locationId].totalSales += entry.sales || 0
      locationStats[entry.locationId].totalCustomers += entry.customers || 0
      locationStats[entry.locationId].days += 1
    })

    return Object.values(locationStats)
      .map(stat => ({
        ...stat,
        avgSales: stat.days > 0 ? stat.totalSales / stat.days : 0,
        avgCustomers: stat.days > 0 ? stat.totalCustomers / stat.days : 0
      }))
      .sort((a, b) => b.avgSales - a.avgSales)
      .slice(0, 5)
  }

  const stats = calculateStats()
  const bestLocations = findBestLocations()

  return (
    <div className="performance-tracker">
      <div className="performance-header">
        <h2>📊 Performance Analytics</h2>
        <div className="header-controls">
          <select 
            value={selectedTimeFrame} 
            onChange={(e) => setSelectedTimeFrame(e.target.value)}
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="all">All Time</option>
          </select>
          <button 
            onClick={() => setShowAddForm(true)}
            className="btn-primary"
          >
            + Add Entry
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <div className="stat-value">${stats.totalSales.toFixed(2)}</div>
            <div className="stat-label">Total Sales</div>
            <div className="stat-sub">${stats.avgSales.toFixed(2)} avg per day</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalCustomers}</div>
            <div className="stat-label">Total Customers</div>
            <div className="stat-sub">{Math.round(stats.avgCustomers)} avg per day</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📅</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalDays}</div>
            <div className="stat-label">Days Tracked</div>
            <div className="stat-sub">
              ${stats.totalCustomers > 0 ? (stats.totalSales / stats.totalCustomers).toFixed(2) : '0'} per customer
            </div>
          </div>
        </div>
      </div>

      <div className="performance-sections">
        <div className="best-locations">
          <h3>🏆 Best Performing Locations</h3>
          {bestLocations.length > 0 ? (
            <div className="location-rankings">
              {bestLocations.map((location, index) => (
                <div key={location.locationId} className="location-rank">
                  <div className="rank-badge">#{index + 1}</div>
                  <div className="location-info">
                    <div className="location-name">{location.name}</div>
                    <div className="location-stats">
                      ${location.avgSales.toFixed(2)}/day • {Math.round(location.avgCustomers)} customers/day
                    </div>
                  </div>
                  <div className="total-days">{location.days} days</div>
                </div>
              ))}
            </div>
          ) : (
            <p>No performance data yet. Add some entries to see your best locations!</p>
          )}
        </div>

        <div className="recent-entries">
          <h3>📝 Recent Entries</h3>
          <div className="entries-list">
            {performanceData.slice(0, 10).map(entry => (
              <div key={entry.id} className="entry-card">
                <div className="entry-header">
                  <div className="entry-location">{getLocationName(entry.locationId)}</div>
                  <div className="entry-weather">{getWeatherIcon(entry.weather)}</div>
                </div>
                <div className="entry-date">
                  {format(entry.date.toDate ? entry.date.toDate() : new Date(entry.date), 'MMM dd, yyyy')}
                </div>
                <div className="entry-stats">
                  <span className="sales">${entry.sales}</span>
                  <span className="customers">{entry.customers} customers</span>
                </div>
                {entry.notes && (
                  <div className="entry-notes">{entry.notes}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {showAddForm && (
        <div className="add-entry-modal">
          <div className="modal-content">
            <h3>Add Performance Entry</h3>
            <form onSubmit={addPerformanceEntry}>
              <div className="form-row">
                <select
                  value={newEntry.locationId}
                  onChange={(e) => setNewEntry({...newEntry, locationId: e.target.value})}
                  required
                >
                  <option value="">Select Location</option>
                  {locations.map(location => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
                <input
                  type="date"
                  value={newEntry.date}
                  onChange={(e) => setNewEntry({...newEntry, date: e.target.value})}
                  required
                />
              </div>

              <div className="form-row">
                <input
                  type="number"
                  step="0.01"
                  placeholder="Total Sales ($)"
                  value={newEntry.sales}
                  onChange={(e) => setNewEntry({...newEntry, sales: e.target.value})}
                  required
                />
                <input
                  type="number"
                  placeholder="Number of Customers"
                  value={newEntry.customers}
                  onChange={(e) => setNewEntry({...newEntry, customers: e.target.value})}
                  required
                />
              </div>

              <div className="form-row">
                <input
                  type="time"
                  value={newEntry.startTime}
                  onChange={(e) => setNewEntry({...newEntry, startTime: e.target.value})}
                />
                <input
                  type="time"
                  value={newEntry.endTime}
                  onChange={(e) => setNewEntry({...newEntry, endTime: e.target.value})}
                />
              </div>

              <select
                value={newEntry.weather}
                onChange={(e) => setNewEntry({...newEntry, weather: e.target.value})}
              >
                <option value="sunny">☀️ Sunny</option>
                <option value="cloudy">☁️ Cloudy</option>
                <option value="rainy">🌧️ Rainy</option>
                <option value="stormy">⛈️ Stormy</option>
                <option value="snowy">❄️ Snowy</option>
              </select>

              <textarea
                placeholder="Notes (optional)"
                value={newEntry.notes}
                onChange={(e) => setNewEntry({...newEntry, notes: e.target.value})}
                rows={3}
              />

              <div className="modal-actions">
                <button type="submit" className="btn-primary">Add Entry</button>
                <button type="button" onClick={() => setShowAddForm(false)} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default PerformanceTracker